import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { MdMenu } from "react-icons/md";
import { LuBell } from "react-icons/lu";
import SideBar from "./SideBar/SideBar";
import NotificationPane from "./Common/NotificationPane";
import ThemeToggle from "./Common/ThemeToggle";
import { APPRoutes } from "../Utils/Route";
import { useAuth } from "../Context/AuthContext";
import { useNotifications } from "../Hooks/useNotifications";
import { getUserInitials } from "../Utils/UserInitials";

export default function Layout() {
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const { logout, decodedToken, token } = useAuth();
    const { notifications, unreadCount, isLoading: isNotificationsLoading, isLoadingMore, hasMore, loadMore, markAsRead, markAsUnread, markAllAsRead, deleteNotification } = useNotifications(token, decodedToken?.user_id);
    const userName = decodedToken?.username || "User";
    const userRole = decodedToken?.role || "User";
    const organizationName = decodedToken?.account_name?.toUpperCase() || "ORGANIZATION";
    const organizationShortName = organizationName.trim().split(/\s+/)[0] || organizationName;
    const userInitials = getUserInitials(userName);
    const handleLogout = () => {
        logout();
        setIsSidebarOpen(false);
        setIsNotificationOpen(false);
        navigate(APPRoutes.LOGIN);
    };

    const handleSidebarToggle = () => {
        if (window.innerWidth >= 992) {
            setIsSidebarCollapsed((prev) => !prev);
            return;
        }

        setIsSidebarOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 992) {
                setIsSidebarOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleEscClose = (event) => {
            if (event.key === "Escape") {
                setIsSidebarOpen(false);
                setIsNotificationOpen(false);
            }
        };

        if (isSidebarOpen || isNotificationOpen) {
            window.addEventListener("keydown", handleEscClose);
        }

        return () => window.removeEventListener("keydown", handleEscClose);
    }, [isSidebarOpen, isNotificationOpen]);

    return (
        <div className="container-fluid px-0 bg-light min-vh-100">
            <div className="row g-0 flex-nowrap">
                <aside className={`d-none d-lg-block p-0 hc-desktop-sidebar overflow-hidden ${isSidebarCollapsed ? "collapsed" : ""}`}>
                    <div className="hc-sidebar-surface hc-desktop-sidebar-inner d-flex flex-column vh-100 overflow-auto p-3">
                        <SideBar onLogout={handleLogout} />
                    </div>
                </aside>

                <main className="col vh-100 d-flex flex-column hc-main-content overflow-x-hidden">
                    <div className="navbar bg-white border-bottom shadow-sm px-3 py-2">
                        <div className="container-fluid px-0 d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className="btn d-inline-flex align-items-center justify-content-center d-lg-none border-0 text-white"
                                    style={{ background: "var(--hc-accent-fill)" }}
                                    onClick={handleSidebarToggle}
                                    aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                                >
                                    <MdMenu size={22} />
                                </button>
                                <span className="d-inline d-md-none fw-bold fs-4 px-2 px-md-0 hc-header-brand-text">{organizationShortName}</span>
                                <span className="d-none d-md-inline fw-bold fs-4 px-2 px-md-0 hc-header-brand-text">{organizationName}</span>
                            </div>

                            <div className="d-flex align-items-center gap-3">
                                <ThemeToggle />

                                <button
                                    type="button"
                                    className="btn btn-link p-0 hc-text-primary position-relative d-flex align-items-center justify-content-center"
                                    onClick={() => setIsNotificationOpen(true)}
                                    aria-label="Notifications"
                                >
                                    <LuBell size={22} strokeWidth={2.5} />
                                    {unreadCount > 0 && (
                                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.65rem', transform: 'translate(-30%, -20%)' }}>
                                            {unreadCount > 99 ? '99+' : unreadCount}
                                            <span className="visually-hidden">unread messages</span>
                                        </span>
                                    )}
                                </button>

                                <span
                                    className="d-inline-flex d-md-none align-items-center justify-content-center rounded-circle text-white fw-semibold"
                                    style={{ width: "30px", height: "30px", background: "var(--hc-accent-fill)" }}
                                >
                                    {userInitials}
                                </span>

                                <div className="d-none d-md-flex align-items-center gap-2 border rounded-pill px-2 py-1 bg-body-tertiary">
                                    <span
                                        className="d-inline-flex align-items-center justify-content-center rounded-circle hc-bg-text-primary text-white fw-semibold"
                                        style={{ width: "30px", height: "30px"  }}
                                    >
                                        {userInitials}
                                    </span>
                                    <div className="d-flex flex-column pe-2 lh-sm">
                                        <span className="fw-semibold text-dark" style={{fontSize: "12px"}}>{userName}</span>
                                        <small className="text-secondary" style={{fontSize: "11px"}}>{userRole}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-2 p-md-3 flex-grow-1 overflow-auto position-relative">
                        <Outlet />
                    </div>
                </main>
            </div>

            <div
                className={`offcanvas offcanvas-start d-lg-none ${isSidebarOpen ? "show" : ""}`}
                style={{ visibility: isSidebarOpen ? "visible" : "hidden" }}
                aria-hidden={!isSidebarOpen}
            >
                <div className="offcanvas-header hc-sidebar-surface border-bottom border-primary-subtle d-flex justify-content-between align-items-center">
                    <h5 className="offcanvas-title text-white">Menu</h5>
                    <button
                        type="button"
                        className="btn btn-danger border-0  d-inline-flex align-items-center justify-content-center"
                        style={{height:"28px"}}
                        onClick={() => setIsSidebarOpen(false)}
                        aria-label="Close"
                    >
                        X
                    </button>
                </div>
                <div className="offcanvas-body hc-sidebar-surface d-flex flex-column p-3">
                    <SideBar onNavigate={() => setIsSidebarOpen(false)} onLogout={handleLogout} />
                </div>
            </div>

            {isSidebarOpen ? (
                <button
                    type="button"
                    className="offcanvas-backdrop fade show d-lg-none border-0 p-0"
                    onClick={() => setIsSidebarOpen(false)}
                    aria-label="Close sidebar"
                />
            ) : null}

            <NotificationPane
                isOpen={isNotificationOpen}
                onClose={() => setIsNotificationOpen(false)}
                notifications={notifications}
                unreadCount={unreadCount}
                isLoading={isNotificationsLoading}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                loadMore={loadMore}
                markAsRead={markAsRead}
                markAsUnread={markAsUnread}
                markAllAsRead={markAllAsRead}
                deleteNotification={deleteNotification}
            />
        </div>
    );
}